import { useMutation, useQueryClient } from '@tanstack/react-query';
import BadgeService, {
  IUpdateBadgeParams,
} from '@clickersphere/core/services/BadgeService';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { BADGES_QUERY_KEY, BadgeQueryData } from './useBadges';

export function useUpdateBadge() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: updateBadgeIsPending, mutateAsync: updateBadgeAsync } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: IUpdateBadgeParams;
      }) => BadgeService.update(id, { ...data }),
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        const oldBadges = queryClient.getQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
        );

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) =>
            old?.map((badge) =>
              badge.id === variables.id
                ? { ...badge, ...variables.data, queryStatus: 'pending' }
                : badge,
            ),
        );

        const oldLeadsQueries = queryClient.getQueriesData<unknown>({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        oldLeadsQueries.forEach(([queryKey, data]) => {
          if (Array.isArray(data)) {
            queryClient.setQueryData(
              queryKey,
              (data as LeadQueryData[]).map((lead) => ({
                ...lead,
                badges: (lead.badges ?? []).map((b) =>
                  b.id === variables.id ? { ...b, ...variables.data } : b,
                ),
              })),
            );
          } else if (data && typeof data === 'object') {
            const lead = data as LeadQueryData;
            queryClient.setQueryData(queryKey, {
              ...lead,
              badges: (lead.badges ?? []).map((b) =>
                b.id === variables.id ? { ...b, ...variables.data } : b,
              ),
            });
          }
        });

        return { oldBadges, oldLeadsQueries };
      },
      onSuccess: async (_data, variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });

        queryClient.setQueryData<BadgeQueryData[]>(
          buildQueryKey(BADGES_QUERY_KEY),
          (old) =>
            old?.map((badge) =>
              badge.id === variables.id
                ? { ...badge, queryStatus: undefined }
                : badge,
            ),
        );
      },
      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(BADGES_QUERY_KEY),
        });
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(LEADS_QUERY_KEY),
        });

        queryClient.setQueryData(
          buildQueryKey(BADGES_QUERY_KEY),
          context?.oldBadges,
        );

        context?.oldLeadsQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      },
    });

  return {
    updateBadgeAsync,
    updateBadgeIsPending,
  };
}
